import type { TermMonthItem, PaymentTerm } from '@makemydeal/dr-platform-types';
import { SpecialPaymentFormula } from '@makemydeal/dr-platform-types';
import type {
    BalloonPaymentGridDataItems,
    BalloonPaymentsSlice,
    BalloonPaymentState,
    UpdateBalloonPaymentGridDataPayload
} from '../types/balloonTypes';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import { getSelectedTerm } from './offerNodeSelectors';
import { getSliceData } from './helpers';
import { getSpecialPaymentFormula, getBalloonType, getBalloonTerm } from './selectedTermSelectors';

// #region Balloon Payment State Selectors
// These selectors read from the balloonPayments state slice

export const getBalloonPayment = (state: BalloonPaymentsSlice): BalloonPaymentState => state.balloonPayments as BalloonPaymentState;

export const getCurrentBalloonPayments = (state: BalloonPaymentsSlice): BalloonPaymentState['currentBalloonPayment'] | null =>
    state.balloonPayments?.currentBalloonPayment ?? null;

export const getBalloonPaymentGridData = (state: BalloonPaymentsSlice): UpdateBalloonPaymentGridDataPayload | null =>
    state.balloonPayments?.balloonPaymentGridData ?? null;

export const getBalloonPaymentGridItems = (state: BalloonPaymentsSlice): BalloonPaymentGridDataItems[] | undefined => {
    const gridData = getBalloonPaymentGridData(state);
    return gridData?.map((col) => col?.items);
};

export const getBalloonPaymentGridToggle = (state: BalloonPaymentsSlice): boolean =>
    state.balloonPayments?.paymentBalloonGridToggle ?? false;

export const getBalloonPaymentGridDownPayments = (state: BalloonPaymentsSlice): number[] => {
    return state.balloonPayments?.balloonDownPayments || [];
};

export const getBalloonPaymentGridDownPaymentsList = (state: BalloonPaymentsSlice) => {
    const downPayments = getBalloonPaymentGridDownPayments(state);
    return downPayments.map((val, key) => ({ key, val }));
};

export const getBalloonPaymentGridTerms = (state: BalloonPaymentsSlice): number[] => {
    return state.balloonPayments?.balloonTerms || [];
};

export const getBalloonPaymentGridTermsList = (state: BalloonPaymentsSlice): TermMonthItem[] => {
    const terms = getBalloonPaymentGridTerms(state);
    return terms.map((val, key) => ({ key, val }));
};

// #endregion Balloon Payment State Selectors

// #region Balloon Payment Term Selectors
// These selectors read from the selected term's balloon payment fields

/**
 * @description Determines if the current deal is a balloon payment deal
 * Reads from selected term's specialPaymentFormula property
 * @param state - Redux state tree
 * @returns true if specialPaymentFormula is Balloon
 */
export const isBalloonDeal = (state: OfferReduxBaseState): boolean => {
    return getSpecialPaymentFormula(state) === SpecialPaymentFormula.Balloon;
};

/**
 * @description Gets the final payment month when balloon is due
 * @param state - Redux state tree
 * @returns Month number or undefined
 */
export const getBalloonFinalPaymentMonth = (state: OfferReduxBaseState): number | undefined => {
    const balloonTerm = getBalloonTerm(state);
    return balloonTerm;
};

/**
 * @description Gets the last month of regular payments before balloon
 * For Term type: same as final month (e.g., 36 payments + balloon on month 36)
 * For Term-1 type: final month - 1 (e.g., 35 payments, balloon on month 36)
 * @param state - Redux state tree
 * @returns Month number or undefined
 */
export const getBalloonRegularPaymentEndMonth = (state: OfferReduxBaseState): number | undefined => {
    const balloonType = getBalloonType(state);
    const finalMonth = getBalloonFinalPaymentMonth(state);

    if (finalMonth === undefined || finalMonth <= 0 || balloonType === undefined) return undefined;

    return balloonType === 'Term-1' ? finalMonth - 1 : finalMonth;
};

/**
 * @description Gets the list of fields specific to balloon payment deals
 * @returns Array of PaymentTerm field keys for balloon data
 */
export const getBalloonFields = (): (keyof PaymentTerm)[] => {
    return ['balloonTerm', 'balloonType', 'balloonAmount'];
};

/**
 * @description Gets all balloon payment data from the selected term
 * Returns undefined if not a balloon deal
 * @param state - Redux state tree
 * @param fields - Optional custom fields to retrieve
 * @returns Partial PaymentTerm with balloon fields or undefined
 */
export const getBalloonData = (
    state: OfferReduxBaseState,
    fields: (keyof PaymentTerm)[] = getBalloonFields()
): Partial<PaymentTerm> | undefined => {
    if (!isBalloonDeal(state)) {
        return undefined;
    }

    const selectedTerm = getSelectedTerm(state);

    return getSliceData(selectedTerm, fields, true);
};

/**
 * @description Default balloon term options (in months)
 * Re-exports balloonTermList from action creators for selector usage
 * @see balloonPaymentActionCreators.balloonTermList
 */
export { balloonTermList as initialBalloonTermMonthList } from '../actions/balloonPaymentActionCreators';

// #endregion Balloon Payment Term Selectors
